import React from "react";
import { useEffect } from "react";
import Box from "@mui/material/Box";
import Plot from "react-plotly.js";
import WebFont from "webfontloader";
import Typography from "@mui/material/Typography";
import Skeleton from "@mui/material/Skeleton";
import Tooltip from "@mui/material/Tooltip";

import MyCountUp from "../UtilityComponents/NumberDisplayComponents/MyCountUp";
import { LinearProgress } from "@mui/material";

import OverviewOptions from "./OverviewOptions";
import OverviewPlotPaperProps from "./OverviewPlotPaperProps";
import periodToXAxisRange from "../UtilityComponents/GraphOptionUtilityComponents/periodToXAxisRange";
import AsOf from "../UtilityComponents/PaperUtilityComponents/AsOf";
import { isMobile } from "react-device-detect";

import { useTheme } from "@mui/material/styles";

const paperPadding = 2;
const summaryStatPaddingTopBottom = 1;
const enterToolTipDelay = 400;

export type PlotJsonType = { [key: string]: any };

function OverviewPlotPaper(props: OverviewPlotPaperProps) {
  const theme = useTheme();

  useEffect(() => {
    WebFont.load({
      google: {
        families: ["Roboto"],
      },
    });
  }, []);

  const overviewPlot = (
    <Plot
      data={props.overviewPlot["data"]}
      layout={Object.assign({}, props.overviewPlot["layout"], {
        width: undefined,
        height: undefined,
        autosize: true,
        font: { ...props.overviewPlot["layout"]["font"], family: "Roboto", size: 16 },
        xaxis: {
          ...props.overviewPlot["layout"]["xaxis"],
          range: periodToXAxisRange(props.historicalPeriod, props.futurePeriod, props.overviewPlot["data"]),
          type: "date",
        },
      })}
      config={Object.assign({}, props.overviewPlot["config"], {
        displayModeBar: false,
        responsive: true,
      })}
      useResizeHandler={true}
      style={{ width: "100%", height: "100%" }}
      onLegendClick={(event: any) => {
        return false;
      }}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", width: "100%" }}>
      <Box
        className="highlight-for-overview-tour-step-1"
        sx={{
          // height: percentOfPaperHeightForSummaryStats, // TODO: set a min height
          py: summaryStatPaddingTopBottom,
          flexGrow: 0,
          width: "100%",
          display: "flex",
          flexWrap: "wrap",
          sizing: "border-box",
          justifyContent: "space-evenly",
        }}
      >
        {[
          {
            description: "Percent Vested",
            value: (
              <Box alignItems="center" sx={{ display: "flex", flexDirection: "row", width: 150 }}>
                <Box sx={{ width: "100%", mr: 1 }}>
                  <LinearProgress
                    variant="determinate"
                    value={props.pctVested * 100}
                    sx={{
                      width: "100%",
                      height: 30,
                      border: 0.5,
                      borderRadius: 1,
                      borderColor: props.pctVested > 0.99 ? theme.palette.success.light : theme.palette.info.light,
                      backgroundColor: theme.palette.background.default,
                      "& .MuiLinearProgress-bar": {
                        backgroundColor: props.pctVested > 0.99 ? theme.palette.success.main : theme.palette.info.main,
                      },
                    }}
                  />
                </Box>
                <MyCountUp
                  value={props.pctVested * 100}
                  lastFullyDrawnValue={props.lastDrawnPctVested}
                  setLastFullyDrawnValue={props.setLastDrawnPctVested}
                  suffix="%"
                  negativeIsError
                  error={props.statsError}
                />
              </Box>
            ),
            tooltip_text: "This is the percentage of your shares that have vested.",
          },
          {
            description: "Available Value",
            value: (
              <MyCountUp
                value={props.vestedValue}
                lastFullyDrawnValue={props.lastDrawnVestedValue}
                setLastFullyDrawnValue={props.setLastDrawnVestedValue}
                prefix="$"
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text:
              "This is the value that is available today, whether from stock that has already vested, cash or cancelling your ESPP.",
          },
          {
            description: "Total Value",
            value: (
              <MyCountUp
                value={props.totalStockValue}
                lastFullyDrawnValue={props.lastDrawnTotalStockValue}
                setLastFullyDrawnValue={props.setLastDrawnTotalStockValue}
                prefix="$"
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text: "This is the total value of all grants (both RSUs and ESPPs) with current market conditions.",
          },
          {
            description: (props.userSymbol ? props.userSymbol + " " : "") + "Stock Price",
            value: (
              <MyCountUp
                value={props.referencePrice}
                lastFullyDrawnValue={props.lastDrawnReferencePrice}
                setLastFullyDrawnValue={props.setLastDrawnReferencePrice}
                prefix="$"
                decimals={2}
                negativeIsError
                error={props.statsError}
              />
            ),
            tooltip_text: "This is the last close price for the referenced stock in the stock plans.",
          },
        ].map((label, index) => (
          <Tooltip
            key={index}
            title={label.tooltip_text}
            placement="bottom-start"
            arrow
            enterDelay={enterToolTipDelay}
            sx={{ flexGrow: 1 }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                sizing: "border-box",
                px: 1,
                flex: {
                  sm: "0 0 50%",
                  md: "none",
                },
              }}
            >
              <Typography variant="h6" noWrap sx={{ color: "info.main" }}>
                {label.description}
              </Typography>
              <Typography variant="h4" noWrap sx={{ paddingBottom: 1 }}>
                {props.plotLoading ? <Skeleton /> : label.value}
              </Typography>
            </Box>
          </Tooltip>
        ))}
      </Box>
      {isMobile ? null : (
        <>
          <Box
            className="highlight-for-overview-tour-step-2"
            sx={{
              flexGrow: 1,
              width: "100%",
              sizing: "border-box",
            }}
          >
            {props.plotLoading ? <Skeleton variant="rectangular" width="100%" height="100%" /> : overviewPlot}
            {/*TODO: can the graph draw itself from left to right as a lead in animation?*/}
          </Box>
          <Box
            sx={{
              flexGrow: 0,
              px: paperPadding,
              py: 1,
              width: "100%",
              sizing: "border-box",
            }}
          >
            <OverviewOptions
              frequency={props.frequency}
              setFrequency={props.setFrequency}
              referenceDateString={props.referenceDateString}
              historicalPeriod={props.historicalPeriod}
              setHistoricalPeriod={props.setHistoricalPeriod}
              futurePeriod={props.futurePeriod}
              setFuturePeriod={props.setFuturePeriod}
              updateOverviewGraph={props.updateOverviewGraph}
            />
          </Box>
          <AsOf referenceDateString={props.referenceDateString} />
        </>
      )}
    </Box>
  );
}

export default OverviewPlotPaper;
